/**
 * 认证状态管理工具函数
 * 提供统一的认证状态清除和管理功能
 */

/**
 * 清除所有认证状态（包括token、refreshToken、用户信息和品牌信息）
 * @param context 调用上下文，用于日志记录
 * @param stopTimer 是否停止token定时刷新检查，默认为true
 */
export function clearAllAuthState(context: string = '未知', stopTimer: boolean = true): void {
  // 停止token定时刷新检查（如果需要）
  if (stopTimer) {
    try {
      // 使用动态导入避免循环依赖
      import('./tokenRefreshTimer').then(({ stopTokenRefreshTimer }) => {
        stopTokenRefreshTimer()
      }).catch(() => {
        // 如果导入失败，忽略错误（可能是在模块加载时调用）
      })
    } catch (error) {
      // 如果导入失败，忽略错误
    }
  }
  
  // 在开发环境中，跳过清除操作以保持localStorage数据
  if (import.meta.env.DEV) {
    console.log(`开发环境：跳过清除认证状态操作 (${context})，保持localStorage数据`)
    return
  }
  
  localStorage.removeItem('apiToken')
  localStorage.removeItem('apiToken_refresh')
  localStorage.removeItem('currentUser')
  localStorage.removeItem('currentBrand')
  
  // 清除品牌store状态（如果store已初始化）
  try {
    // 直接清除localStorage中的品牌信息，避免动态导入
    localStorage.removeItem('currentBrand')
  } catch (error) {
    // 如果清除品牌store失败，忽略错误
  }
}

/**
 * 清理损坏的token
 * 检查并移除格式不正确的token
 */
export function cleanupCorruptedTokens(): void {
  // 在开发环境中，跳过token清理以保持localStorage数据
  if (import.meta.env.DEV) {
    console.log('开发环境：跳过token清理，保持localStorage数据')
    return
  }
  
  const token = localStorage.getItem('apiToken')
  const refreshToken = localStorage.getItem('apiToken_refresh')
  
  // 检查主token
  if (token && !isValidTokenFormat(token)) {
    console.warn('检测到损坏的主token，正在清理...')
    localStorage.removeItem('apiToken')
  }
  
  // 检查refreshToken
  if (refreshToken && !isValidTokenFormat(refreshToken)) {
    console.warn('检测到损坏的refreshToken，正在清理...')
    localStorage.removeItem('apiToken_refresh')
  }
}

/**
 * 检查token格式是否有效
 * @param token JWT token字符串或refreshToken字符串
 * @returns 是否有效
 */
function isValidTokenFormat(token: string): boolean {
  if (!token || typeof token !== 'string') {
    return false
  }
  
  // 清理token，移除可能的空格和引号
  const cleanToken = token.trim().replace(/^["']|["']$/g, '')
  
  // 检查是否是JWT格式（包含两个点分隔符）
  if (cleanToken.includes('.')) {
    const parts = cleanToken.split('.')
    if (parts.length !== 3) {
      return false
    }
    
    if (!parts[0] || !parts[1] || !parts[2]) {
      return false
    }
    
    // 检查payload是否只包含有效的base64字符（包括URL安全的base64）
    // JWT使用URL安全的base64，其中-和_分别替换了+和/
    const base64Regex = /^[A-Za-z0-9+/_-]*={0,2}$/
    const isValidBase64 = base64Regex.test(parts[1])
    
    // 如果base64验证失败，记录详细信息用于调试
    if (!isValidBase64) {
      console.warn('JWT Token payload base64验证失败:', {
        originalToken: token,
        cleanToken: cleanToken,
        payload: parts[1],
        payloadLength: parts[1].length,
        firstChars: parts[1].substring(0, 20),
        lastChars: parts[1].substring(parts[1].length - 10)
      })
    }
    
    return isValidBase64
  } else {
    // 如果不是JWT格式，检查是否是有效的base64编码字符串（用于refreshToken）
    // 检查是否只包含有效的base64字符，包括padding字符=
    const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/
    const isValidBase64 = base64Regex.test(cleanToken)
    
    // 如果base64验证失败，记录详细信息用于调试
    if (!isValidBase64) {
      console.warn('非JWT Token base64验证失败:', {
        originalToken: token,
        cleanToken: cleanToken,
        tokenLength: cleanToken.length,
        firstChars: cleanToken.substring(0, 20),
        lastChars: cleanToken.substring(cleanToken.length - 10)
      })
    }
    
    return isValidBase64
  }
}

/**
 * 检查是否有有效的认证状态
 * @returns 是否有有效的认证状态
 */
export function hasValidAuthState(): boolean {
  const token = localStorage.getItem('apiToken')
  const currentUser = localStorage.getItem('currentUser')
  return !!(token && currentUser)
}

/**
 * 检查JWT token是否过期
 * @param token JWT token字符串
 * @returns 是否过期
 */
export function isTokenExpired(token: string): boolean {
  try {
    // 检查token格式是否正确
    if (!token || typeof token !== 'string') {
      console.warn('Token为空或不是字符串类型')
      return true
    }

    // 检查是否包含JWT分隔符
    if (!token.includes('.')) {
      console.warn('Token格式不正确，缺少JWT分隔符')
      return true
    }

    const parts = token.split('.')
    if (parts.length !== 3) {
      console.warn('JWT token格式错误，应该包含3个部分')
      return true
    }

    // 检查每个部分是否为空
    if (!parts[0] || !parts[1] || !parts[2]) {
      console.warn('JWT token的某个部分为空')
      return true
    }

    // 解码payload部分
    const payload = parts[1]
    
    // 检查payload是否只包含有效的base64字符（包括URL安全的base64）
    const base64Regex = /^[A-Za-z0-9+/_-]*={0,2}$/
    if (!base64Regex.test(payload)) {
      console.warn('Token payload包含无效的base64字符:', payload)
      console.warn('Token payload长度:', payload.length)
      console.warn('Token payload字符:', Array.from(payload).slice(0, 20).join(''))
      return true
    }

    // 将URL安全的base64转换为标准base64
    const standardBase64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    
    // 添加padding以确保base64解码正确
    const paddedPayload = standardBase64 + '='.repeat((4 - standardBase64.length % 4) % 4)
    
    // 使用try-catch包装atob，避免InvalidCharacterError
    let decodedPayload: string
    try {
      decodedPayload = atob(paddedPayload)
    } catch (decodeError) {
      console.warn('Base64解码失败，可能是token已损坏:', decodeError)
      return true
    }

    // 尝试解析JSON
    let payloadObj: any
    try {
      payloadObj = JSON.parse(decodedPayload)
    } catch (parseError) {
      console.warn('Token payload JSON解析失败:', parseError)
      return true
    }

    const currentTime = Math.floor(Date.now() / 1000)
    
    // 检查是否有exp字段
    if (!payloadObj.exp) {
      console.warn('Token中没有过期时间字段')
      return true
    }

    // 检查exp字段是否为数字
    if (typeof payloadObj.exp !== 'number') {
      console.warn('Token过期时间字段不是数字类型')
      return true
    }

    // 提前5分钟认为过期，给刷新留出时间
    return payloadObj.exp < (currentTime + 300)
  } catch (error) {
    console.warn('解析token失败:', error)
    return true // 解析失败认为已过期
  }
}

/**
 * 强制跳转到登录页面
 * @param redirectPath 登录后需要跳转的路径
 */
export function forceRedirectToLogin(redirectPath?: string): void {
  if (redirectPath && redirectPath !== '/login') {
    sessionStorage.setItem('redirectAfterLogin', redirectPath)
  }
  
  // 使用 window.location.href 确保完全刷新页面
  window.location.href = '/login'
}

/**
 * 处理令牌刷新失败的情况
 * @param context 调用上下文
 * @param currentPath 当前路径，用于登录后重定向
 */
export function handleTokenRefreshFailure(context: string, currentPath?: string): void {
  // 强制清除所有认证状态，即使在开发环境也要清除（因为刷新失败意味着token无效）
  // 先停止定时器
  try {
    import('./tokenRefreshTimer').then(({ stopTokenRefreshTimer }) => {
      stopTokenRefreshTimer()
    }).catch(() => {
      // 如果导入失败，忽略错误
    })
  } catch (error) {
    // 如果导入失败，忽略错误
  }
  
  // 强制清除localStorage中的认证信息，无论环境
  localStorage.removeItem('apiToken')
  localStorage.removeItem('apiToken_refresh')
  localStorage.removeItem('currentUser')
  localStorage.removeItem('currentBrand')
  
  console.log(`❌ Token刷新失败，已清除所有认证状态 (${context})`)
  
  // 如果当前不在登录页，则跳转到登录页
  const currentPathToUse = currentPath || window.location.pathname
  if (currentPathToUse !== '/login') {
    forceRedirectToLogin(currentPathToUse)
  } else {
    // 如果已经在登录页，仍然需要清除认证状态，但不需要跳转
    console.log(`已在登录页，无需跳转 (${context})`)
  }
}

/**
 * 手动清理所有认证状态（用于调试和故障排除）
 * 可以在浏览器控制台中调用此函数来清理损坏的认证状态
 */
export function manualCleanup(): void {
  console.log('开始手动清理认证状态...')
  clearAllAuthState('手动清理')
  cleanupCorruptedTokens()
  console.log('认证状态清理完成，请刷新页面')
}

// 将清理函数暴露到全局，方便在控制台调用
if (typeof window !== 'undefined') {
  (window as any).cleanupAuth = manualCleanup
  ;(window as any).decodeToken = decodeTokenInfo
}

/**
 * 解码并显示JWT token信息（用于调试）
 * @param token JWT token字符串
 */
export function decodeTokenInfo(token: string): void {
  try {
    if (!token || typeof token !== 'string') {
      console.log('Token为空或不是字符串')
      return
    }

    const parts = token.split('.')
    if (parts.length !== 3) {
      console.log('Token格式错误，应该包含3个部分')
      return
    }

    console.log('Token结构:')
    console.log('Header:', parts[0])
    console.log('Payload:', parts[1])
    console.log('Signature:', parts[2])

    // 解码header
    try {
      const headerBase64 = parts[0].replace(/-/g, '+').replace(/_/g, '/')
      const headerPadded = headerBase64 + '='.repeat((4 - headerBase64.length % 4) % 4)
      const headerDecoded = atob(headerPadded)
      const headerObj = JSON.parse(headerDecoded)
      console.log('Header (解码):', headerObj)
    } catch (error) {
      console.log('Header解码失败:', error)
    }

    // 解码payload
    try {
      const payloadBase64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
      const payloadPadded = payloadBase64 + '='.repeat((4 - payloadBase64.length % 4) % 4)
      const payloadDecoded = atob(payloadPadded)
      const payloadObj = JSON.parse(payloadDecoded)
      console.log('Payload (解码):', payloadObj)
      
      // 显示过期时间
      if (payloadObj.exp) {
        const expDate = new Date(payloadObj.exp * 1000)
        console.log('过期时间:', expDate.toLocaleString())
        console.log('是否过期:', payloadObj.exp < Math.floor(Date.now() / 1000))
      }
    } catch (error) {
      console.log('Payload解码失败:', error)
    }
  } catch (error) {
    console.log('Token解析失败:', error)
  }
}